// Un-Comment Console.logs for debugging if needed
/**
 * Utility to get CSP nonce from meta tag
 * Use this when you need to dynamically add inline scripts/styles
 */

/**
 * Gets the CSP nonce value from the meta tag
 * @returns {string} The nonce value or empty string if not found
 */
export const getCspNonce = (): string => {
  const metaTag = document.querySelector('meta[name="csp-nonce"]');
  const nonceValue = metaTag?.getAttribute('content') || '';

  // console.log('getCspNonce called:');
  // console.log('  Meta tag found:', !!metaTag);
  // console.log('  Nonce value:', nonceValue);
  // console.log('  Meta tag element:', metaTag);

  return nonceValue;
};

/**
 * Creates a script element with CSP nonce
 * @param scriptContent - The JavaScript code to execute
 * @param id - Optional ID for the script element
 * @returns The created script element
 */
export const createNoncedScript = (
  scriptContent: string,
  id?: string,
): HTMLScriptElement => {
  const script = document.createElement('script');
  const nonce = getCspNonce();

  if (nonce) {
    script.setAttribute('nonce', nonce);
  }

  if (id) {
    script.id = id;
  }

  script.textContent = scriptContent;
  return script;
};

/**
 * Creates a style element with CSP nonce
 * @param cssContent - The CSS code to apply
 * @param id - Optional ID for the style element
 * @returns The created style element
 */
export const createNoncedStyle = (
  cssContent: string,
  id?: string,
): HTMLStyleElement => {
  const style = document.createElement('style');
  const nonce = getCspNonce();

  if (nonce) {
    style.setAttribute('nonce', nonce);
  }

  if (id) {
    style.id = id;
  }

  style.textContent = cssContent;
  return style;
};

/**
 * Executes inline JavaScript with proper CSP nonce
 * @param code - JavaScript code to execute
 */
export const executeWithNonce = (code: string): void => {
  const script = createNoncedScript(code);
  document.head.appendChild(script);
  // Clean up after execution
  script.remove();
};

/**
 * Example usage:
 *
 * import { getCspNonce, createNoncedScript, createNoncedStyle } from './Utils/cspNonce';
 *
 * // Get nonce for manual use
 * const nonce = getCspNonce();
 * console.log('Current CSP nonce:', nonce);
 *
 * // Add inline script with nonce
 * const script = createNoncedScript(`
 *   console.log('This script has proper CSP nonce');
 * `);
 * document.head.appendChild(script);
 *
 * // Add inline styles with nonce
 * const style = createNoncedStyle(`
 *   .custom-class { color: red; }
 * `);
 * document.head.appendChild(style);
 *
 * // Execute code immediately
 * executeWithNonce(`
 *   console.log('Executed with nonce');
 * `);
 */
